<template>
  <div class="vacation-types layout">
    <vacation-type-sidesheet ref="vacationTypeModal" @remove="removeType" />
    <remove-type-modal ref="removeTypeModal" @remove="removeType" />

    <div class="layout-header">
      <div class="layout-title">Типы отпусков</div>
      <div class="layout-header-actions">
        <div class="layout-header-action">
          <c-button2
            size="small"
            :icon="{
              icon: 'bookmark',
              activeColor: 'white',
            }"
            :tooltip="{
              text: 'Добавить в закладки',
              activeText: 'Убрать из закладок',
            }"
            active-color="blue"
            :active="bookmark"
            @click="bookmark = !bookmark"
          />
        </div>
        <div class="layout-header-action">
          <c-button2
            :responsive="true"
            size="small"
            label="Новый тип отпуска"
            :icon="{ icon: 'plus', color: 'primary' }"
            @click="addType"
          />
        </div>
      </div>
    </div>

    <div class="vacation-types__content">
      <c-input
        v-model="search"
        class="vacation-types__search"
        placeholder="Поиск по типу"
        type="text"
        :clearable="true"
        icon="search"
      />

      <div v-if="!loading && !vacationTypes.length" class="vacation-types__empty">
        {{
          search ? "Ничего не найдено. Попробуйте изменить параметры поиска." : "Не добавлено ни одного типа отпуска"
        }}
      </div>

      <c-loader
        v-if="loading"
        class="vacation-types__loader"
        :active="loading"
        label="Данные загружаются"
        :height="300"
      ></c-loader>

      <div class="vacation-types__list">
        <draggable
          v-model="vacationTypes"
          tag="div"
          ghost-class="sortable__ghost"
          class="item-container"
          group="list"
          :force-fallback="true"
          @change="resortTypes"
        >
          <div v-for="type in vacationTypes" v-show="isTypeVisible(type)" :key="type.id" class="vacation-type">
            <div class="vacation-type__wrapper">
              <div class="vacation-type__data">
                <span class="vacation-type__name" style="white-space: nowrap">
                  <span style="white-space: normal">
                    {{ type.name }}
                    <span v-if="type.timesheet_code" class="vacation-type__code">
                      ({{ type.timesheet_code }})
                    </span> </span
                  >&nbsp;<span
                    v-b-popover.hover.blur.top="'Цвет в графике отпусков'"
                    class="vacation-type__color"
                    :style="{
                      color: type.color || defaultColor,
                    }"
                  ></span>
                </span>

                <span v-if="type.description" class="vacation-type__description">
                  {{ type.description }}
                </span>
              </div>
              <div class="vacation-type__actions">
                <c-button2
                  v-b-popover.hover.blur.top="'Редактировать'"
                  class="vacation-type__edit"
                  size="iconic"
                  icon="edit"
                  @click="editType(type)"
                />

                <c-button2
                  v-if="type.canDelete"
                  v-b-popover.hover.blur.top="'Удалить'"
                  class="vacation-type__remove"
                  size="iconic"
                  :class="{
                    'vacation-type__remove--disabled': !!type.is_sick,
                  }"
                  :disabled="!!type.is_sick"
                  :icon="{ icon: 'trash', color: 'secondary' }"
                  @click="onRemoveTypeClick(type)"
                />

                <c-button2
                  v-if="!type.canDelete"
                  :id="'vacation-type-blocked-' + type.id"
                  class="vacation-type__remove vacation-type__remove--disabled"
                  size="iconic"
                  :icon="{ icon: 'trash', color: 'secondary' }"
                  @click.stop
                />

                <b-popover
                  placement="top"
                  container="app"
                  boundary="window"
                  :target="'vacation-type-blocked-' + type.id"
                  triggers="hover blur"
                >
                  <div class="reference-uniform-category-empty-tooltip">
                    <svg
                      class="reference-uniform-category-empty-tooltip-icon"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="7" cy="7" r="6.5" fill="white" stroke="#FF238D" />
                      <circle cx="7" cy="10" r="1" fill="#FF238D" />
                      <rect x="6" y="3" width="2" height="5" rx="1" fill="#FF238D" />
                    </svg>
                    <div class="reference-uniform-category-empty-tooltip-text">
                      В данный момент удаление невозможно, так как тип отпуска используется в разделе "Графики отпусков
                      и больничных"
                    </div>
                  </div>
                </b-popover>

                <c-button-2
                  v-b-popover.hover.blur.top="'Переместить'"
                  class="vacation-type__move handle"
                  size="iconic"
                  icon="arrows-constructor"
                />
              </div>
            </div>
            <div v-if="type.description" class="vacation-type__description">
              {{ type.description }}
            </div>
          </div>
        </draggable>
      </div>
    </div>

    <top-button root=".layout"></top-button>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import CButton2 from "@/components/controls2/Button";
import CInput from "@/components/controls2/Input";
import TopButton from "@/components/top-button";
import CLoader from "@/components/loader";
import VacationTypeSidesheet from "./components/vacation-type-sidesheet";
import RemoveTypeModal from "./components/remove-type-modal";

export default {
  name: "reference-vacation-types",

  components: {
    draggable,
    CButton2,
    CLoader,
    CInput,
    VacationTypeSidesheet,
    RemoveTypeModal,
    TopButton,
  },

  data() {
    return {
      bookmark: false,
      search: "",
      loading: true,
      defaultColor: "#DFE4EF",
    };
  },

  computed: {
    vacationTypes: {
      get() {
        return this.$store.state.referenceVacationTypes.types;
      },
      set(value) {
        this.$store.commit("referenceVacationTypes/updateVacationTypes", value);
      },
    },
    normalizedSearch() {
      return this.search.trim().toLowerCase();
    },
  },

  created() {
    this.$store.dispatch("referenceVacationTypes/loadTypes").then(() => {
      this.loading = false;
    });
  },

  methods: {
    addType() {
      this.$refs.vacationTypeModal.open();
    },

    editType(type) {
      this.$refs.vacationTypeModal.open({
        vacationType: type,
      });
    },

    onRemoveTypeClick(type) {
      this.$refs.removeTypeModal.open({
        type,
      });
    },

    removeType({ type }) {
      this.$store.dispatch("referenceVacationTypes/removeType", {
        id: type.id,
      });
    },

    resortTypes() {
      this.$store.dispatch("referenceVacationTypes/resortTypes", {
        types: this.vacationTypes.map((t) => t.id),
      });
    },
    isTypeVisible(type) {
      return (
        (type.name || "").toLowerCase().includes(this.normalizedSearch) ||
        (type.description || "").toLowerCase().includes(this.normalizedSearch)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
.vacation-types {
  @include onlyStandartMobile {
    padding-bottom: 50px;
  }
  .layout-header {
    margin-bottom: 20px;
    align-items: center;
  }
  &__search {
    max-width: 466px;

    & /deep/ .c-input-input::placeholder {
      font-style: italic;
    }
  }
  &__list {
    max-width: 1160px;
    margin-top: 33px;

    @include mobileAndTablet {
      margin-top: 20px;
    }

    @include onlyMobile {
      margin-left: -21px;
      margin-right: -21px;
    }
  }
  &__empty {
    font-size: 15px;
    color: $color-label;
    line-height: 1.2;
    margin-top: 33px;

    @include mobileAndTablet {
      margin-top: 20px;
    }
  }
  &__loader {
    margin-top: 33px;

    @include mobileAndTablet {
      margin-top: 20px;
    }
  }
  .vacation-type {
    width: 100%;
    background: #ffffff;
    border: 1px solid #dfe4ef;
    margin-top: -1px;
    font-size: 13px;
    color: #000;
    display: flex;
    //min-height: 35px;

    &__code {
      color: $color-label;
    }

    & > .vacation-type__description {
      display: none;
      padding: 0 16px 12px;
      margin-top: -8px;
    }

    @include onlyStandartMobile {
      display: block;

      .vacation-type__wrapper {
        align-items: flex-start;
      }

      & > .vacation-type__description {
        display: block;
      }

      &__data {
        .vacation-type__description {
          display: none;
        }
      }
    }

    &__wrapper {
      display: flex;
      height: 100%;
      width: 100%;
      align-items: center;
      padding-left: 15px;
      padding-right: 10px;
      padding-top: 6px;
      padding-bottom: 6px;
    }
    &__data {
      flex-grow: 1;
      padding-right: 20px;
      overflow: hidden;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      line-height: 1.2;
    }
    &__name {
      margin-right: 15px;
      margin-top: 1px;
      margin-bottom: 1px;
    }
    &__color {
      display: inline;
      position: relative;
      vertical-align: middle;
      margin-left: 13px;

      &:before {
        content: "";
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: currentColor;
        position: relative;
        top: -1px;
      }
    }
    &__description {
      color: $color-label;
      font-size: 11px;
      margin-top: 1px;
      margin-bottom: 1px;
    }
    &__actions {
      display: flex;
      flex-shrink: 0;
    }
    &__remove {
      margin-top: -2px;
      margin-left: 13px;

      &--disabled {
        opacity: 0.6;
        cursor: default;
      }
    }
    &__move {
      margin-left: 12px;
    }
  }
  .sortable__ghost {
    height: 5px;
    min-height: 0;
    overflow: hidden;
    opacity: 1;
    width: inherit !important;
    background: $color-primary;

    * {
      display: none;
    }
  }

  .sortable-drag {
    min-height: 35px !important;
    z-index: 10 !important;
    opacity: 1 !important;
    background: white !important;
    box-shadow: 0px 3px 16px rgba(0, 102, 255, 0.7) !important;
    height: auto !important;

    .vacation-type__move {
      & /deep/ {
        svg path {
          fill: $color-primary;
        }
      }
    }
  }
}
</style>
